import { Injectable } from "@angular/core";
import { APP_CONFIG, Env, Region } from "../../environment";
import { DocumentResult } from "../../model/myria/app/DocumentResult";
import { HttpProvider } from "../http/http";
import { COUNTRY_SETTINGS, COUNTRY_SETTINGS_ENABLE_VALUE, FLOW_STEPS, KYC_METHODS, KYC_STATUS, ONFIDO_MAXIMUM_ATTEMPTS_RET_VAL, CUSTOMER_SETTINGS, KYC_ATTEMPT_STATUS, KYC_ERROR_STATUS, EKYC_PROCESSOR, EKYC_PROCESSOR_TYPE, AllowedDocuments } from "./app-variables";
import { EkycFlowState } from "./ekyc-flow-state";
import { FeatureFlagTypes, UtilsProvider } from "./utils-provider";
import { LoggerProvider, LoggerEvent } from "../logger/logger";
import { TranslateService } from "@ngx-translate/core";
import { LoginProvider } from "./login-provider";
import { OrderProvider } from "../order/order";
import { RouteProvider } from "../route/route";
import { ConfigProvider } from "../config/config";
import { IOrder } from "../../model/models/order/order-interface";
import { LoadingProvider } from "../../providers/loading/loading";
import { IdentityVerification, User } from "../../model/myria/app/user";
import { AuthProvider } from "../authorization";
import { AlertController, Platform } from 'ionic-angular';
import { CustomersProvider } from "./customer-provider/customers-provider";
import { CustomerProvider } from "../customer/customer";
import { SegmentProvider } from "../../providers/analytics/segment";
import { AnalyticsEventType, ContactModalReasons, SegmentLocationNames } from "../../model/types/analytics.const";
import { SendFlowProvider } from "../send-flow/send-flow";
import { CustomerIdentificationProcessorType } from "../../model/types/customer-identification-processor-type";
import { ICustomerIdentificationProcessor } from "../../model/models/customer/customer.identification.interface";
import { IdentityVerificationProvider } from '../../providers/identity-verification/identity-verification';
import { MissingTransactionInfoType } from "../../model/types/missing-transaction-info-type";
import { INavInfo } from "../../model/models/layout/nav-info.interface";
import { NumberFormatter } from "../../pipes/currency-pipe";
import { PaymentMethodType } from "../../model/types/payment-method-type";
import { ResourceProvider } from "../resource/resource";
import { PreKycIdType } from '../../model/types/pre-kyc-id-type';
import { CustomerIdentificationStatusType } from '../../model/types/customer-identification-status-type';

declare let window;
// TODO: Tipar objetos
@Injectable()
export class OnfidoProvider {

  private static readonly POST_MODAL_NAVIGATION_DELAY_MS = 300;

  public checkId: string;

  private token: string;
  private applicantId: string;
  private steps: string[];

  private isFromProfile: boolean;
  private isFromCarousel = false;
  private isFromSendMoney: boolean;
  ekycType = null;
  fallbackEkycType = null;
  
  // to retreive state after bankid/signicat deeplink
  previousOnSuccess;
  previousAnalyticsLocation;
  previousFromSettingsPage;
  private triggerSourcePage: INavInfo | null;

  pendingEkycSkipAmountUpdated: boolean = false;
  private suppressFailureClosePop: boolean = false;

  // NZ document selection; reset on every flow entry so a previous choice is never sent again
  preKycIdType: PreKycIdType | null = null;

  constructor(
    private http: HttpProvider,
    private utilsProvider: UtilsProvider,
    private translate: TranslateService,
    private routeProvider: RouteProvider,
    private orderProvider: OrderProvider,
    private platform: Platform,
    private loading: LoadingProvider,
    private alertCtrl: AlertController,
    private loginProvider: LoginProvider,
    private sendFlowProvider: SendFlowProvider,
    private customersProvider: CustomersProvider,
    private customerProvider: CustomerProvider,
    private analytics: SegmentProvider,
    private identityProvider: IdentityVerificationProvider,
    private currencyFormatter: NumberFormatter,
    private resourceProvider: ResourceProvider
    ) {
  }

  // check if user has available attempts of any ekyc processor
  public async canAccessByAttempt(forceReload: boolean = false, checkAttemptsProcessor: CustomerIdentificationProcessorType = null ) {
    if (await this.loginProvider.hasMultipleEkycProviders()) {
      var identityVerifications = await this.loginProvider.getIdentityVerificationsAsync();
      if (checkAttemptsProcessor && identityVerifications.find(e => e.Processor === this.loginProvider.getKYCProcessorNumber(checkAttemptsProcessor) && e.AttemptsLeft > 0)) // check if any attempts left of specified processor
        return true;
      else if (checkAttemptsProcessor == null && identityVerifications.find(e => e.AttemptsLeft > 0))
        return true;
      else
        return false;
    }
    else
      var kycStatus = await this.loginProvider.getIdentityVerificationAsync(forceReload);
    if (kycStatus)
      return (kycStatus.AttemptsLeft > 0);
    return true;
  }

  // check if user has exhausted attempts of any ekyc processor
  public async hasAnyZeroAttempts(forceReload: boolean = false, checkAttemptsProcessor: CustomerIdentificationProcessorType = null) {
    if (await this.loginProvider.hasMultipleEkycProviders()) {
      var identityVerifications = await this.loginProvider.getIdentityVerificationsAsync();
      if (checkAttemptsProcessor && identityVerifications.find(e => e.Processor === this.loginProvider.getKYCProcessorNumber(checkAttemptsProcessor) && e.AttemptsLeft === 0)) // check if no attempts left of specified processor
        return true;
      else if (checkAttemptsProcessor == null && identityVerifications.find(e => e.AttemptsLeft === 0))
        return true;
      else
        return false;
    }
    else
      var kycStatus = await this.loginProvider.getIdentityVerificationAsync(forceReload);
    if (kycStatus)
      return (kycStatus.AttemptsLeft === 0);
    return true;
  }

  public startOnFido(document: string): Promise<DocumentResult> {
    LoggerProvider.info(this, 'Start OnFido for document ' + document + ' and token ' + this.token);
    return new Promise(async (resolve, reject) => {
      if (this.platform.is('cordova')) {
        const options = {
          countryCode: APP_CONFIG.region.defaultCountryFromOnfido,
          documentAllowed: document,
          flow_steps: this.steps,
          token: this.token
        };

        window.cordova.plugins.onfido.init((res: DocumentResult) => {
          LoggerProvider.info(this, 'Finished OnFido', res);
          const objOrder = this.orderProvider.getCurrentOrder();
          this.analytics.trackEvent({ eventName: AnalyticsEventType.IdentityVerificationCompleted, analyticsData: { verificationMethod: KYC_METHODS.ONFIDO, verificationStatus: 'success', destinationCountry: objOrder && objOrder.countryTo, amount: objOrder && SegmentProvider.formatNumber(objOrder.amountFrom), currency: objOrder && objOrder.currencyFrom, amountUSD: objOrder && SegmentProvider.formatNumber(objOrder.sendAmountUSD) } });
          resolve(res);
        }, (err) => {
          LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'Failed to init OnFido');
          reject();
        }, options);
      }
    });
  }

  public initOnfido(analyticsLocation): Promise<any> {
    return new Promise((resolve, reject) => {
      Promise.all([this.http.get(APP_CONFIG.API_URL + 'api/kyc/onfido/generatetoken' + '?workflowstep=' + encodeURIComponent(analyticsLocation)), this.getFlowSteps()])
      .then((res) => {
        if(res[0].Result.RetVal == ONFIDO_MAXIMUM_ATTEMPTS_RET_VAL) {
          reject();
        }
        else {
          this.token = res[0].Value.Token;
          this.applicantId = res[0].Value.KycId;

          // QAs have problems with ZScaler and Onfido since they need to turn ZScaler off for Onfido BUT they need ZScaler to access the API
          // Normally it's no problem since they can turn it off during accessing Onfido and before uploading the photo but the US requires an
          // extra consent screen which requires an extra request to Onfido
          // Hence only for the non production versions and only for the US we have an extra step so they can turn ZScaler off
          if (APP_CONFIG.env !== Env.PROD && APP_CONFIG.region.defaultCountryFrom === 'US') {
            this.loading.hide();
            const confirm = this.alertCtrl.create({
              message: 'If available please turn off ZScaler before proceeding',
              buttons: [
                {
                  text: 'Got it',
                  handler: () => {
                    resolve(true);
                    return true;
                  }
                }
              ]
            });
            confirm.setMode('ios');
            confirm.present();
          }
          else
            resolve(true);
        }
      },
      () => reject());
    });
  }

  public getFlowSteps(): Promise<string[]> {
    return new Promise(async (resolve, reject) => {
      const steps = [FLOW_STEPS.DOCUMENT];
      this.utilsProvider.hasCountrySettings(APP_CONFIG.region.regionId,
                                              [COUNTRY_SETTINGS.KYC_INCLUDE_FACE_RECOGNITION,
                                              COUNTRY_SETTINGS.ONFIDO_FACE_VIDEO_ENABLED])
      .subscribe((settingsValue) => {
            const selfieCS = parseInt(settingsValue[0]) === COUNTRY_SETTINGS_ENABLE_VALUE;
            const videoCS = parseInt(settingsValue[1]) === COUNTRY_SETTINGS_ENABLE_VALUE;
            if(selfieCS) steps.push(FLOW_STEPS.FACE);
            else if(videoCS) steps.push(FLOW_STEPS.FACE_VIDEO);
            else steps.push(FLOW_STEPS.FACE); // fallback to face
          steps.push(FLOW_STEPS.FINAL);
          this.steps = steps;
          resolve(steps);
      });
    });
  }
  
  public getDocumentList(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      if (APP_CONFIG.region.region === Region.MY) {
        this.getDocumentsForRegionMY()
        .then(resolve)
        .catch(reject);
      } else {
        this.getDocumentsForOtherRegions()
        .then(resolve)
        .catch(reject);
      }
    });
  }

  private async getDocumentsForRegionMY(): Promise<string[]> {
    const objUser: User = await this.loginProvider.getUserAsync()

    if (objUser && objUser.Customers && objUser.Customers.length > 0 && objUser.Customers[0].eKYCAllowedDocuments)
      return this.parseDocuments(objUser.Customers[0].eKYCAllowedDocuments);
    else
      throw new Error('No eKYC allowed documents found.');
  }

  private getDocumentsForOtherRegions(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      this.utilsProvider.hasCountrySetting(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.ONFIDO_ALLOWED_DOCUMENTS)
        .subscribe((settingValue: string) => {
          if (settingValue)
            resolve(this.parseDocuments(settingValue));
          else
            reject();
        }, reject);
    });
  }

  private parseDocuments(documents: string): string[] {
    return documents.replace(/\s/g, "").toLowerCase().split(",");
  }

  public sendOnfidoInformation(analyticsLocation): Promise<any> {
    return new Promise((resolve, reject) => {
      const location = encodeURIComponent(analyticsLocation)
      this.http.get(APP_CONFIG.API_URL + 'api/kyc/onfido/verify?kycId=' + this.applicantId + '&location=' + location).then(
        res => {
          if (res.Value && res.Value.VerifyId){
            this.checkId = res.Value.VerifyId;
            resolve(true);
          } else {
            reject();
          }
        },
        () => reject()
      );
    });
  }

  public getOnfidoStatus(repeat: boolean = true, accertifyOrderNumber = null): Promise<any> {
    return new Promise((resolve) => {
      let timer = null;
      if (repeat) {
        timer = {
          timeout: setTimeout(() => {
            timer.canceled = true;
            resolve({ Result: KYC_ATTEMPT_STATUS.IN_PROGRESS });
          }, ConfigProvider.settings.onfidoTimeout),
          canceled: false
        }
      }
      this.requestStatus(timer, resolve, accertifyOrderNumber);
    });
  }

  public getAccertifyVeriffStatus(repeat: boolean = true, orderNo): Promise<any> {
    return new Promise((resolve) => {
      let timer = null;
      if (repeat) {
        timer = {
          timeout: setTimeout(() => {
            timer.canceled = true;
            resolve({ Result: KYC_ATTEMPT_STATUS.IN_PROGRESS });
          }, ConfigProvider.settings.onfidoTimeout),
          canceled: false
        }
      }
      this.requestAccertifyVeriffStatus(timer, resolve, orderNo);
    });
  }

  private async requestStatus(timer: any, resolve: any, accertifyOrderNumber = null) {
    try {
      var kycStatus = await this.loginProvider.getIdentityVerificationAsync(true);
      if (await this.loginProvider.hasMultipleEkycProviders()) {
        if (accertifyOrderNumber || kycStatus.Status != KYC_ATTEMPT_STATUS.VERIFIED) {
          LoggerProvider.info(this, `Checking all identity verifications (${accertifyOrderNumber})`);
          var identityVerifications = await this.loginProvider.getIdentityVerificationsAsync();
          let element;
          if (accertifyOrderNumber)
            element = identityVerifications.find(e => e.Processor == 2080) // match with Veriff for accertify
          else {
            // look vor Veriff/Onfido match since eventually it's not the primary identityverification object but still when Veriff/Onfido has passed it's fine
            element = identityVerifications.find(e => ((e.Processor == 2070 || e.Processor == 2080) && e.Status == KYC_ATTEMPT_STATUS.VERIFIED))
          }

          if (element) {
            LoggerProvider.info(this, 'Found identity verification result in identity verifications array', element);
            kycStatus = element;
          }
        }
      }
      if (timer && timer.canceled) {
        clearTimeout(timer.timeout);
        return;
      } // already rejected

      if(kycStatus.Status == KYC_ATTEMPT_STATUS.VERIFIED) {
        if (timer)
          timer.canceled = true;
        kycStatus.Result = KYC_ATTEMPT_STATUS.VERIFIED;
        AuthProvider.lastKycResult = kycStatus.Result;
        resolve(kycStatus);
      }
      else if(kycStatus.Status != KYC_ATTEMPT_STATUS.IN_PROGRESS) {
        kycStatus.Result = (kycStatus.KycResult === KYC_STATUS.KYC_SUSPECTED ? KYC_STATUS.KYC_SUSPECTED : KYC_STATUS.KYC_CONSIDER);
        AuthProvider.lastKycResult = kycStatus.Result;
        resolve(kycStatus);
      }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Error while getting status');
      //resolve({ Result: KYC_STATUS.KYC_ERROR }); TODO use reject when we received a fitting response code but not in general because it could be a temporary application problem
    }
    if (timer && !timer.canceled) {
      window.setTimeout(() => {
        this.requestStatus(timer, resolve, accertifyOrderNumber);
      }, 500);
    }
    else if (!timer)
      resolve({ Result: KYC_ATTEMPT_STATUS.IN_PROGRESS });
  }

  private async requestAccertifyVeriffStatus(timer: any, resolve: any, orderNo) {
    try {
      var kycStatus = await this.orderProvider.getAccertifyFraudOrderStatus(orderNo);
      if (timer && timer.canceled) {
        clearTimeout(timer.timeout);
        return;
      } // already rejected
      if (kycStatus && kycStatus.Setting != KYC_ATTEMPT_STATUS.IN_PROGRESS) { // kycStatus.Setting for accertify veriff as opposed to kycStatus.Status
        if (timer)
          timer.canceled = true;
        if (kycStatus.Setting == KYC_ATTEMPT_STATUS.VERIFIED)
          kycStatus.Result = KYC_ATTEMPT_STATUS.VERIFIED
        resolve(kycStatus);
      }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Error while getting status');
    }
    if (timer && !timer.canceled) {
      window.setTimeout(() => {
        this.requestAccertifyVeriffStatus(timer, resolve, orderNo);
      }, 500);
    }
    else if (!timer)
      resolve({ Result: KYC_ATTEMPT_STATUS.IN_PROGRESS });

  }

  public requireUpdateIdOnStore(customer) {
    if (APP_CONFIG.region.region === Region.SG)
      return true;

    return false;
  }


  public async isOnfidoEnable() {
    const setting = await this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.KYC);
    if (setting === KYC_METHODS.ONFIDO || setting === KYC_METHODS.OCR || setting === KYC_METHODS.VERIFF || setting === KYC_METHODS.SIGNICAT) {
      this.ekycType = setting;
      this.fallbackEkycType = await this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.FALLBACK_EKYC_METHOD);
      LoggerProvider.info(this, 'Fallback Ekyc type is', this.fallbackEkycType);
      if (APP_CONFIG.region.region === Region.MY || APP_CONFIG.region.region === Region.SN) {
        const customer = await this.customerProvider.getCustomer();
        if (this.requireUpdateIdOnStore(customer)) {
          LoggerProvider.warning(this, LoggerEvent.ComponentException, 'Invalid flow - user shouldn\'t require Onfido !');
          return false;
        }
      }
      return true;
    }

    if (APP_CONFIG.region.region === Region.CL && setting === KYC_METHODS.DIGITAL_ID_VERIFICATION) {
      this.ekycType = KYC_METHODS.VERIFF;
      return true;
    }
    return false;
  }

  // function to decide which ekyc to show from the 2, IF enabled
  public async showVeriffOrOnfido(missing = null, isUSFallback = false) {
    try {
      const settingKYC = await this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.KYC);
      const fallbackKYC = await this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.FALLBACK_EKYC_METHOD);
      const higherLimitKYC = await this.utilsProvider.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.HIGHER_LIMIT_EKYC_PROCESSOR);
      // only for the US, we use Veriff for both fallback and higher limit but we should check from country setting
      if (isUSFallback && fallbackKYC)
        return fallbackKYC;
      // currently this missing error only for the US
      if (missing === MissingTransactionInfoType.HigherLimitIdentityVerificationRequired && higherLimitKYC) {
        LoggerProvider.info(this, `Show higher limit KYC ${higherLimitKYC}`);
        return higherLimitKYC;
      } else if ((APP_CONFIG.region.region === Region.CA ||
                  APP_CONFIG.region.region === Region.UK ||
                  APP_CONFIG.region.region === Region.AU) && fallbackKYC) {
        LoggerProvider.info(this, `Show fallback KYC ${fallbackKYC}`);
        return fallbackKYC;
      } else if (APP_CONFIG.region.region === Region.US || APP_CONFIG.region.region === Region.PR) {
        // only for security as US/PR should be handled by higherLimitKYC
        LoggerProvider.info(this, `Show Veriff for US/PR although it should be handled by higherLimitKYC`);
        return KYC_METHODS.VERIFF;
      }
      else if (this.loginProvider.isSignicatCountry() && (fallbackKYC === KYC_METHODS.VERIFF || settingKYC === KYC_METHODS.VERIFF)) {
        LoggerProvider.info(this, `Show Veriff for Signicat country`);
        return KYC_METHODS.VERIFF;
      } else if (this.loginProvider.isSignicatCountry()) {
        LoggerProvider.info(this, `Show Onfido for Signicat country`);
        return KYC_METHODS.ONFIDO;
      } else if (APP_CONFIG.region.region === Region.CL && settingKYC === KYC_METHODS.DIGITAL_ID_VERIFICATION) {
        LoggerProvider.info(this, `Show Higher Limit KYC for CL ${higherLimitKYC}`);
        return higherLimitKYC;
      } else {
        LoggerProvider.info(this, `Show KYC from setting ${settingKYC}`);
        return settingKYC;
      }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Error while getting Veriff or Onfido ekyc');
    }
  }

  public async isKycRequired() {
    if (APP_CONFIG.region.oldRemittanceLogin) return false;
    // TODO TEMPORARY !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    // eventually can be removed
    // since before APP_CONFIG.region.oldRemittanceLogin was set to true for the US but since it has changed it would affect the flow which 
    // could have unexpected effects hence let's treat it the same way for now
    if (APP_CONFIG.region.region === Region.US || APP_CONFIG.region.region === Region.PR) {
      LoggerProvider.info(this, 'Skiped isKycRequired for US/PR');
      return false;
    }
    // We will use validate call to check ekyc requirement for Lithuania licensed countries
    const isEUEkycCountry = await this.utilsProvider.checkLithuaniaCountryLicense();
    if(isEUEkycCountry) {
      LoggerProvider.info(this, 'Skiped isKycRequired for EU Validate ekyc countries');
      return false;
    }
    const identityVerification = await this.loginProvider.getIdentityVerificationAsync();
    if (identityVerification && identityVerification.Status === KYC_ATTEMPT_STATUS.VERIFIED) return false;
    if (await this.loginProvider.hasMultipleEkycProviders()) {
      const identityVerifications = await this.loginProvider.getIdentityVerificationsAsync();
      if (identityVerifications.find(e => e.Status === KYC_ATTEMPT_STATUS.VERIFIED))
        return false;
    }
    else {
      return this.isOnfidoEnable();
    }
  }

  public storeOrder(checkId = this.checkId) {
    const order = this.orderProvider.getCurrentOrder();
    if(order && order.recipient) {
      order.pendingEKyc = true;
      order.onfidoId = checkId;
      this.orderProvider.storeOrder(this.routeProvider.pages.OnfidoPendingPage, this.translate.instant('page.activityPage.iDVerificationInProgressHeader'), this.translate.instant('page.activityPage.iDVerificationInProgressContent'));
    }
  }

  public async handleResult(result: any, order: any, fromTabBtn?: boolean) {
    // the decision arrived after the customer left the flow, so restore the stored transfer for the NZ screens
    const isLateNzFailure = APP_CONFIG.region.region === Region.NZ && order && order.recipient
      && result.Result !== KYC_ATTEMPT_STATUS.VERIFIED && result.Result !== KYC_ATTEMPT_STATUS.IN_PROGRESS;
    if (isLateNzFailure) {
      this.orderProvider.setCurrentOrder(order);
      this.setOnfidoFlow(true, false);
    }

    if (result.Result === KYC_ATTEMPT_STATUS.VERIFIED) {
      try {
        await this.customerProvider.getCustomer(true);
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get customer in Onfido handle result');
      }
      this.routeProvider.openModal(this.routeProvider.pages.OnfidoVerifiedPage, {
        primaryButtonAction: async () => {
          LoggerProvider.info(this, 'Continue OnFido after successful verification', JSON.stringify(order));
          if(order && order.currencyFrom && order.recipient) {
            this.orderProvider.setCurrentOrder(order);
            this.routeProvider.replace(this.routeProvider.pages.SendSummaryPage);
          }else{
            this.orderProvider.setCurrentOrder(null);
            this.routeProvider.replace(this.routeProvider.pages.CheckPricesPage);
          }

        }
      });
    }
    else if (result.Result === KYC_ATTEMPT_STATUS.IN_PROGRESS)
      if(fromTabBtn && APP_CONFIG.region.region !== Region.US)
        this.routeProvider.openModal(this.routeProvider.pages.OnfidoPendingPage);
      else if(!fromTabBtn)
        this.routeProvider.openModal(this.routeProvider.pages.OnfidoPendingPage);
      else
        this.sendFlowProvider.sendMoneyBtn();
    else if (result.Result === KYC_STATUS.KYC_SUSPECTED){
      this.checkIfVeriffError(result);
    }else
      this.goOnfidoError(result, null, order, true);
  }

  public async goOnfidoError(result, onSuccess = null, order: IOrder = null, modal: boolean = false, onIdologyFailure = null, accertifyOrderNumber = null) {
    const kycProcessorType = (result) ? result.Processor : undefined;
    const processor = this.getProcessorName(kycProcessorType);

    this.analytics.trackEvent({ eventName: AnalyticsEventType.IdentityVerificationCompleted, analyticsData: {
      verificationMethod: processor,
      verificationStatus: 'error',
      destinationCountry: order && order.countryTo,
      amount: order && SegmentProvider.formatNumber(order.amountFrom),
      currency: order && order.currencyFrom,
      amountUSD: order && SegmentProvider.formatNumber(order.sendAmountUSD)
    }});

    if (onIdologyFailure && !this.isNzEkyc()) { // to override behaviour when failing veriff after idology
      this.routeProvider.replace(this.routeProvider.pages.OnfidoServerTimeoutErrorPage, {
        primaryButtonAction: () => onIdologyFailure(),
        showRightClose: false
      })
    }
    else if (accertifyOrderNumber) { // Always bring to activity screen when accertify veriff fails
      this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomePage);
    }
    else {
      this.releasePromotion();
      const KycErrorCode = (result) ? result.KycErrorCode : undefined;
      const analyticLocation = this.routeProvider.hasPage(this.routeProvider.pages.SendSummaryPage) ? SegmentLocationNames.SendMoney : SegmentLocationNames.Settings;

      if (!onSuccess) {
        onSuccess = () => {
          if (order && order.currencyFrom) {
            this.orderProvider.setCurrentOrder(order);
            this.routeProvider.replace(this.routeProvider.pages.SendSummaryPage);
          }
          else
            this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage)
        };
      }
      let targetPage = this.routeProvider.pages.OnfidoServerTimeoutErrorPage;
      let targetParams = {};
      let canAccess = await this.canAccessByAttempt();
      const isFromProfileSettings = this.isFromProfileFlow();
      const isFromSendMoney = this.isFromSendMoneyFlow();

      if (this.isNzEkyc()) {
        if (await this.hasAnyZeroAttempts(true, CustomerIdentificationProcessorType.Veriff))
          this.showNzActionRequired(modal);
        else
          this.showNzTryAgain(modal);
        return;
      }

      if (canAccess && isFromProfileSettings) {
        await this.showProfileSettingsEkycFailure(analyticLocation, modal);
        return;
      }

      if (canAccess && isFromSendMoney) {
        await this.showSmfEkycFailure(analyticLocation, modal);
        return;
      }

      if (!canAccess || KycErrorCode == undefined) {
        targetPage = this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF;
        targetParams = { onClose: () => this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomePage) }
        if (this.routeProvider.hasPage(this.routeProvider.pages.SettingsIdPage, true)) {
          targetParams = { onClose: () => this.routeProvider.popTo(this.routeProvider.pages.SettingsIdPage) }
        }
      }
      else if (KycErrorCode === KYC_ERROR_STATUS.DOCUMENT_EXPIRED) {
        targetPage = this.routeProvider.pages.OnfidoExpirationErrorPage;
        targetParams = {
          primaryButtonAction: async () => {
            canAccess = await this.canAccessByAttempt();
            if (canAccess) {
              if (await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry())
                this.routeProvider.replace(this.routeProvider.pages.EkycMethodSelectPage, { onSuccess: onSuccess, analyticLocation: analyticLocation });
              else
                this.routeProvider.replace(this.routeProvider.pages.OnfidoInfoPage, { onSuccess: onSuccess });
            }
            else
              this.routeProvider.setRoot(this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF);
          }
        };
      }
      else if (KycErrorCode === KYC_ERROR_STATUS.DOCUMENT_IS_NOT_SUPPORTED) {
        targetPage = this.routeProvider.pages.OnfidoRejectedErrorPage;
        targetParams = {
          primaryButtonAction: async () => {
            canAccess = await this.canAccessByAttempt();
            if (canAccess) {
              if (await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry())
                this.routeProvider.replace(this.routeProvider.pages.EkycMethodSelectPage, { onSuccess: onSuccess, analyticLocation: analyticLocation });
              else
                this.routeProvider.replace(this.routeProvider.pages.OnfidoInfoPage, { onSuccess: onSuccess });
            }
            else
              this.routeProvider.setRoot(this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF);
          }
        };
      }
      else if (KycErrorCode === KYC_ERROR_STATUS.DOCUMENT_NOT_READABLE) {
        targetPage = this.routeProvider.pages.OnfidoImageIntegrityErrorPage;
        targetParams = {
          primaryButtonAction: async () => {
            canAccess = await this.canAccessByAttempt();
            if (canAccess) {
              if (await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry())
                this.routeProvider.replace(this.routeProvider.pages.EkycMethodSelectPage, { onSuccess: onSuccess, analyticLocation: analyticLocation });
              else
                this.routeProvider.replace(this.routeProvider.pages.OnfidoInfoPage, { onSuccess: onSuccess });
            } else
              this.routeProvider.setRoot(this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF);
          }
        };
      }
      else if (APP_CONFIG.region.region === Region.US) {
        // US generic retryable error: "Try again" restarts verification from the first eKYC screen
        targetParams = {
          primaryButtonAction: async () => {
            if (await this.canAccessByAttempt())
              // preserve the wallet-onboarding flag so the retry keeps the wallet result screens
              this.routeProvider.replace(this.routeProvider.pages.OnfidoInfoPage, { onSuccess: onSuccess, usWalletFlow: EkycFlowState.isWalletOnboarding });
            else
              this.routeProvider.setRoot(this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF);
          }
        };
      }

      if (modal)
        this.routeProvider.openModal(targetPage, targetParams);
      else
        this.routeProvider.replace(targetPage, targetParams);
    }
  }

  //check if suspect kyc status is from veriff
  public async checkIfVeriffError(res, onIdologyFailure = null) {
    var user = await this.loginProvider.getUserAsync();

    if (user.KYCAttempts && user.KYCAttempts[0].OperationResultId && user.KYCAttempts[0].ProviderId === 60) {
      var params = {
        title: null,
        msg: null,
        primaryButtonAction: () => this.redirectToSuspected(res, onIdologyFailure)
      }
      switch (user.KYCAttempts[0].OperationResultId) {
        case 201:
          params.title = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailDocMissingHeader');
          params.msg = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailDocMissingText');
          break;
        case 204:
          params.title = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailImageQuality');
          params.msg = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailImageQualityText');
          break;
        case 205:
          params.title = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailDocumentDamaged');
          params.msg = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailDocumentDamagedText');
          break;
        case 206:
          params.title = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailDocumentType');
          params.msg = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailDocumentTypeText');
          break;
        case 207:
          params.title = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailDocumentExpired');
          params.msg = this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailDocumentExpiredText');
          break;
        default:
          break;
      }
      this.routeProvider.replace(this.routeProvider.pages.VeriffErrorCodePage, params)
    }
    else
      this.redirectToSuspected(res, onIdologyFailure)
  }

  public async redirectToSuspected(result, onIdologyFailure = null) {
    if (onIdologyFailure && !this.isNzEkyc()) { // to override behaviour when failing veriff after idology
      this.routeProvider.replace(this.routeProvider.pages.OnfidoServerTimeoutErrorPage, {
        primaryButtonAction: () => onIdologyFailure(),
        showRightClose: false
      })
    }
    else {
      this.releasePromotion();
      const attemptsLeft = result.AttemptsLeft;
      const analyticLocation = this.routeProvider.hasPage(this.routeProvider.pages.SendSummaryPage) ? SegmentLocationNames.SendMoney : SegmentLocationNames.Settings;
      const isSignicatEnabled = await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry();
      if (attemptsLeft && attemptsLeft > 0) {
        const isFromProfileSettings = this.isFromProfileFlow();
        const isFromSendMoney = this.isFromSendMoneyFlow();
        if (isFromProfileSettings) {
          await this.showProfileSettingsEkycFailure(analyticLocation, true);
        } else if (isFromSendMoney) {
          await this.showSmfEkycFailure(analyticLocation, true);
        } else {
          this.routeProvider.setRoot(this.routeProvider.pages.OnfidoRejectedPage, {
            msg: this.translate.instant('page.activityPage.issueWithIDMessage', { attempts: attemptsLeft }),
            onClose: () => this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomePage),
            primaryButtonAction: () => {
              if (isSignicatEnabled)
                this.routeProvider.push(this.routeProvider.pages.EkycMethodSelectPage, { showBack: true, analyticLocation: analyticLocation })
              else
                this.routeProvider.push(this.routeProvider.pages.OnfidoInfoPage, { showBack: true })
            }
          });
        }
      }
      else {
        await this.routeExhaustedAttempts(analyticLocation, true, ContactModalReasons.IdVerificationAttempts);
      }
    }
  }

  private async showProfileSettingsEkycFailure(analyticLocation: string, modal: boolean = false) {
    if (this.isNzEkyc()) {
      this.showNzTryAgain(modal);
      return;
    }

    const isSignicatEnabled = await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry();

    this.openEkycFailureAlert(modal, {
      showRightClose: true,
      primaryButtonAction: () => this.goToPreVeriffForProfileSettings(isSignicatEnabled, analyticLocation),
      onClose: () => this.goBackToPersonalDetails()
    });
  }

  private goToPreVeriffForProfileSettings(isSignicatEnabled: boolean, analyticLocation: string) {
    const params: any = { showBack: true, fromSettingsPage: true };

    if (isSignicatEnabled) {
      params.analyticsLocation = analyticLocation;
      this.routeProvider.replace(this.routeProvider.pages.EkycMethodSelectPage, params);
    } else {
      this.routeProvider.replace(this.routeProvider.pages.OnfidoInfoPage, params);
    }
  }

  private goBackToPersonalDetails() {
    if (this.routeProvider.hasPage(this.routeProvider.pages.SettingsPersonalPage, true)) {
      this.routeProvider.popTo(this.routeProvider.pages.SettingsPersonalPage);
    } else if (this.routeProvider.hasPage(this.routeProvider.pages.SettingsIdPage, true)) {
      this.routeProvider.popTo(this.routeProvider.pages.SettingsIdPage);
    } else {
      // setRoot can take longer than a fixed delay, and a push before it finishes is wiped by the new root
      Promise.resolve(this.routeProvider.setRoot(this.routeProvider.pages.TabsHomePage))
        .then(() => this.routeProvider.push(this.routeProvider.pages.SettingsPersonalPage, { personal: true }));
    }
  }

  isNzEkyc(): boolean {
    return APP_CONFIG.region.region === Region.NZ && (this.isFromSendMoney || this.isFromProfile);
  }

  getNzAnalyticsLocation(): SegmentLocationNames {
    if (this.isFromSendMoney)
      return SegmentLocationNames.SendMoney;
    return this.isFromCarousel ? SegmentLocationNames.Carousel : SegmentLocationNames.Settings;
  }

  // ModalAlertPage calls onClose whenever it is left, so actions that navigate away set suppressFailureClosePop first.
  // X calls onClose without the page and then the page leaves, so X also suppresses that second call.
  // A screen closed by setRoot never leaves, so each new failure screen starts with the flag cleared.
  private closeNzFailure(fromCloseButton: boolean) {
    if (this.suppressFailureClosePop) {
      this.suppressFailureClosePop = false;
      return;
    }
    if (fromCloseButton)
      this.suppressFailureClosePop = true;
    if (this.isFromCarousel)
      this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
    else if (!this.isFromSendMoney)
      this.goBackToPersonalDetails();
    else if (this.routeProvider.hasPage(this.routeProvider.pages.SendSummaryPage, true))
      this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
    else // a late decision opened over Activity
      this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
  }

  private onNzVerified() {
    if (this.isFromSendMoney) {
      // a late decision has no confirmed transfer, so the stored order goes back to the summary
      const page = this.routeProvider.hasPage(this.routeProvider.pages.SendSummaryPage, true)
        ? this.routeProvider.pages.SendFinishPage
        : this.routeProvider.pages.SendSummaryPage;
      this.routeProvider.replace(page);
      return;
    }
    this.loginProvider.isProfileCallNotRequired = false;
    this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
  }

  private getNzDocumentSelectionParams(preKycIdType: PreKycIdType | null) {
    return {
      preKycIdType: preKycIdType,
      analyticsLocation: this.getNzAnalyticsLocation(),
      fromSettingsPage: !this.isFromSendMoney,
      onSuccess: () => this.onNzVerified(),
      onFailure: async () => {
        if (await this.hasAnyZeroAttempts(true, CustomerIdentificationProcessorType.Veriff))
          this.showNzActionRequired();
        else
          this.routeProvider.replace(this.routeProvider.pages.NzDocumentSelectionPage, this.getNzDocumentSelectionParams(this.preKycIdType));
      },
    };
  }

  private showNzTryAgain(modal: boolean = false) {
    this.suppressFailureClosePop = false;
    const preKycIdType = this.preKycIdType;
    let leftByTryAgain = false;
    const targetParams = {
      showFullPage: true,
      img: 'assets/imgs/verification-failed.svg',
      title: this.translate.instant('ekyc.veriffFailure.generic.title'),
      subtitle: this.translate.instant('ekyc.veriffFailure.generic.body'),
      primaryButtonText: this.translate.instant('ekyc.veriffFailure.generic.button'),
      dismissFromPrimary: false,
      primaryButtonAction: () => {
        leftByTryAgain = true;
        this.routeProvider.replace(this.routeProvider.pages.NzDocumentSelectionPage, this.getNzDocumentSelectionParams(preKycIdType));
      },
      showRightClose: true,
      onClose: (page?) => {
        if (!leftByTryAgain)
          this.closeNzFailure(!page);
      },
    };

    if (modal)
      this.routeProvider.openModal(this.routeProvider.pages.ModalAlertPage, targetParams);
    else
      this.routeProvider.replace(this.routeProvider.pages.ModalAlertPage, targetParams);
  }

  showNzActionRequired(modal: boolean = false) {
    this.suppressFailureClosePop = false;
    const targetParams = {
      showFullPage: true,
      img: 'assets/imgs/verification-failed.svg',
      title: this.translate.instant('ekyc.veriffFailure.exhausted.title'),
      subtitle: this.translate.instant('ekyc.veriffFailure.exhausted.body'),
      primaryButtonText: this.translate.instant('ekyc.veriffFailure.exhausted.findStoresLink'),
      dismissFromPrimary: false,
      primaryButtonAction: () => {
        this.suppressFailureClosePop = true;
        this.openFindStoresNearby();
      },
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('ekyc.veriffFailure.exhausted.supportButton'),
      showZendeskHelpPage: true, // secondary opens the help centre
      dismissFromSecondary: false, // keep the screen behind the help centre
      contactCTAReason: ContactModalReasons.AccountBlockedIdVerification,
      trackEvent: AnalyticsEventType.ContactModalDisplayed,
      trackParams: { location: this.getNzAnalyticsLocation(), reason: ContactModalReasons.AccountBlockedIdVerification },
      showRightClose: true,
      onClose: (page?) => this.closeNzFailure(!page),
    };

    if (modal)
      this.routeProvider.openModal(this.routeProvider.pages.ModalAlertPage, targetParams);
    else
      this.routeProvider.replace(this.routeProvider.pages.ModalAlertPage, targetParams);
  }

  private async showSmfEkycFailure(analyticLocation: string, modal: boolean = false) {
    if (this.isNzEkyc()) {
      this.showNzTryAgain(modal);
      return;
    }

    const isSignicatEnabled = await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry();

    this.openEkycFailureAlert(modal, {
      hideHeader: true,
      primaryButtonAction: () => this.goToPreVeriffForSmf(isSignicatEnabled, analyticLocation),
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.identityVerification.action.backToTransferSummary.label'),
      dismissFromSecondary: false,
      secondaryButtonAction: async () => this.openSmfSkipBottomSheetFromFailure(isSignicatEnabled, analyticLocation)
    });
  }

  private goToPreVeriffForSmf(isSignicatEnabled: boolean, analyticLocation: string) {
    if (isSignicatEnabled)
      this.routeProvider.replace(this.routeProvider.pages.EkycMethodSelectPage, { showBack: true, analyticsLocation: analyticLocation });
    else
      this.routeProvider.replace(this.routeProvider.pages.OnfidoInfoPage, { showBack: true });
  }

  private openSmfSkipBottomSheetFromFailure(isSignicatEnabled: boolean, analyticLocation: string) {
    const order = this.orderProvider.getCurrentOrder();
    const isEKYCSkipAllowed = order && order.isEKYCSkipAllowed === true;
    const remainingAmount = order && order.remainingAmountWithouteKYC ? order.remainingAmountWithouteKYC : 0;

    if (isEKYCSkipAllowed && remainingAmount > 0) {
      this.openSkipAllowedBottomSheet(remainingAmount);
    } else {
      this.openSkipNotAllowedBottomSheet(() => this.goToPreVeriffForSmf(isSignicatEnabled, analyticLocation));
    }
  }

  private openEkycFailureAlert(modal: boolean, overrides: any) {
    const targetPage = this.routeProvider.pages.ModalAlertPage;
    const targetParams = {
      ...overrides,
      showFullPage: true,
      img: 'assets/imgs/verification-failed.svg',
      title: this.translate.instant('page.identityVerification.eKYCFailure.generic.title'),
      subtitle: this.translate.instant('page.identityVerification.eKYCFailure.attemptsRemaining.body'),
      primaryButtonText: this.translate.instant('page.identityVerification.eKYCFailure.action.tryAgain.label')
    };

    if (modal)
      this.routeProvider.openModal(targetPage, targetParams);
    else
      this.routeProvider.replace(targetPage, targetParams);
  }

  public openSkipAllowedBottomSheet(remainingAmountWithouteKYC: number) {
    const { currency, formatted } = this.getRemainingAmountDisplay(remainingAmountWithouteKYC);

    this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, {
      image: 'assets/imgs/cash-icon.png',
      title: this.translate.instant('page.identityVerification.bottomSheet.skipFlow.skipAllowed.title'),
      subtitle: this.translate.instant('page.identityVerification.bottomSheet.skipFlow.skipAllowed.description', { remainingAmount: formatted, currency: currency }),
      primaryButtonText: this.translate.instant('page.identityVerification.action.updateAmount.label'),
      dismissFromPrimary: false,
      primaryButtonAction: async (_rp, modalRef) => {
        await modalRef.viewCtrl.dismiss();
        setTimeout(() => this.openChangeAmountModalForSkip(remainingAmountWithouteKYC), OnfidoProvider.POST_MODAL_NAVIGATION_DELAY_MS);
      },
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.identityVerification.action.exitForNow.label'),
      dismissFromSecondary: false,
      secondaryButtonAction: async (_rp, modalRef) => {
        await modalRef.viewCtrl.dismiss();
        setTimeout(() => this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage), OnfidoProvider.POST_MODAL_NAVIGATION_DELAY_MS);
      },
    }, null);
  }

  public openSkipNotAllowedBottomSheet(onContinueVerification: (() => void) | null = null) {
    this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, {
      image: 'assets/imgs/verification-progress.svg',
      title: this.translate.instant('page.identityVerification.bottomSheet.skipFlow.skipNotAllowed.title'),
      subtitle: this.translate.instant('page.identityVerification.bottomSheet.skipFlow.skipNotAllowed.description'),
      primaryButtonText: this.translate.instant('page.identityVerification.action.continueVerification.label'),
      dismissFromPrimary: !onContinueVerification,
      primaryButtonAction: this.buildContinueVerificationAction(onContinueVerification),
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.identityVerification.action.backToTransferSummary.label'),
      dismissFromSecondary: false,
      secondaryButtonAction: async (_rp, modalRef) => {
        await modalRef.viewCtrl.dismiss();
        setTimeout(() => this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage), OnfidoProvider.POST_MODAL_NAVIGATION_DELAY_MS);
      },
    }, null);
  }

  private buildContinueVerificationAction(onContinueVerification: (() => void) | null) {
    if (!onContinueVerification)
      return null;

    return async (_rp, modalRef) => {
      await modalRef.viewCtrl.dismiss();
      setTimeout(() => onContinueVerification(), OnfidoProvider.POST_MODAL_NAVIGATION_DELAY_MS);
    };
  }

  private openChangeAmountModalForSkip(remainingAmountWithouteKYC: number) {
    const order = this.orderProvider.getCurrentOrder();
    if (!order) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'openChangeAmountModalForSkip — no current order, aborting eKYC skip change-amount flow');
      return;
    }

    const { currency, formatted } = this.getRemainingAmountDisplay(remainingAmountWithouteKYC);
    const skipAllowedLimitText = this.translate.instant('page.identityVerification.snackbar.skipAllowedLimit.text', { amount: formatted, currency: currency });

    this.routeProvider.openModal(this.routeProvider.pages.SendChangeAmountPage, {
      currency: currency,
      amountFrom: remainingAmountWithouteKYC,
      amountTo: order.amountTo,
      rate: order.exchangeRate,
      ratePromo: order.calculatedRatePromo,
      skipAllowedLimitText: skipAllowedLimitText
    },
    (result) => this.onSkipChangeAmountConfirmed(order, result),
    null, false);
  }

  private getRemainingAmountDisplay(amount: number) {
    const order = this.orderProvider.getCurrentOrder();
    const currency = order && order.currencyFrom ? order.currencyFrom : '';

    return { currency, formatted: this.currencyFormatter.transform(amount, currency) };
  }

  private onSkipChangeAmountConfirmed(order: IOrder, result: any) {
    if (!result || !result.calculate) {
      LoggerProvider.info(this, 'onSkipChangeAmountConfirmed — change-amount modal cancelled / no calculation');
      return;
    }

    const updated: IOrder = {
      ...order,
      shouldCalcAmountFrom: false,
      amountFrom: result.amountSend,
      amountTo: result.amountReceive
    };

    this.orderProvider.setCurrentOrder(updated);
    this.pendingEkycSkipAmountUpdated = true;

    setTimeout(() => this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage), OnfidoProvider.POST_MODAL_NAVIGATION_DELAY_MS);
  }

  private isPayInStoreAllowedForCountry(): boolean {
    return this.resourceProvider.isPayInStoreEnabled();
  }

  private async showSmfEkycExhaustedSkipAllowed(analyticLocation: string, modal: boolean = false, trackReason?: ContactModalReasons) {
    const order = this.orderProvider.getCurrentOrder();
    const remainingAmount = order && order.remainingAmountWithouteKYC ? order.remainingAmountWithouteKYC : 0;
    const { currency, formatted } = this.getRemainingAmountDisplay(remainingAmount);

    this.openExhaustedFailureAlert(modal, {
      showRightClose: true,
      subtitle: this.translate.instant('page.identityVerification.eKYCFailure.attemptsExceeded.skipAllowed.body', { remainingAmount: formatted, currency: currency }),
      primaryButtonText: this.translate.instant('page.identityVerification.action.updateAmount.label'),
      dismissFromPrimary: false,
      primaryButtonAction: async (_rp, modalRef) => {
        await modalRef.viewCtrl.dismiss();
        setTimeout(() => this.openChangeAmountModalForSkip(remainingAmount), OnfidoProvider.POST_MODAL_NAVIGATION_DELAY_MS);
      },
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.identityVerification.eKYCFailure.action.contactSupport.label'),
      showZendeskHelpPage: true,
      contactCTAReason: ContactModalReasons.AccountBlockedIdVerification,
      trackEvent: AnalyticsEventType.ContactModalDisplayed,
      trackParams: { location: analyticLocation, reason: trackReason || ContactModalReasons.AccountBlockedIdVerification },
      onClose: () => {
        if (this.routeProvider.hasPage(this.routeProvider.pages.SendSummaryPage, true))
          this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
      }
    });
  }

  private async showSmfEkycExhaustedNoSkip(analyticLocation: string, modal: boolean = false, trackReason?: ContactModalReasons) {
    const allowsPayInStore = this.isPayInStoreAllowedForCountry();

    const overrides: any = {
      showRightClose: true,
      subtitle: this.translate.instant(allowsPayInStore
        ? 'page.identityVerification.eKYCFailure.attemptsExceeded.changeAmountOrSupport.body'
        : 'page.identityVerification.eKYCFailure.attemptsExceeded.supportOnly.body'),
      primaryButtonText: this.translate.instant('page.identityVerification.eKYCFailure.action.contactSupport.label'),
      showZendeskHelpPage: true,
      contactCTAReason: ContactModalReasons.AccountBlockedIdVerification,
      trackEvent: AnalyticsEventType.ContactModalDisplayed,
      trackParams: { location: analyticLocation, reason: trackReason || ContactModalReasons.AccountBlockedIdVerification },
      onClose: () => {
        if (this.suppressFailureClosePop) {
          this.suppressFailureClosePop = false;
          return;
        }

        if (this.routeProvider.hasPage(this.routeProvider.pages.SendSummaryPage, true))
          this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
      }
    };

    if (allowsPayInStore) {
      overrides.showSecondaryButton = true;
      overrides.secondaryButtonText = this.translate.instant('page.identityVerification.eKYCFailure.action.payInStore.label');
      overrides.dismissFromSecondary = false;
      overrides.secondaryButtonAction = () => {
        this.suppressFailureClosePop = true;
        this.openExchangeRateMayChangeSheet(() => this.goToCalculatorWithPayInStorePreselected());
      };
      overrides.additionalHyperlinkBtnText = this.translate.instant('page.identityVerification.eKYCFailure.helper.findStoresNearby.text');
      overrides.additionalHyperlinkBtnIcon = 'assets/icons/info-orange.svg';
      overrides.additionalHyperlinkBtnIsPrimary = true;
      overrides.additionalHyperlinkBtnAction = () => {
        this.suppressFailureClosePop = true;
        this.openFindStoresNearby();
      };
    }

    this.openExhaustedFailureAlert(modal, overrides);
  }

  private async showProfileEkycExhausted(analyticLocation: string, modal: boolean = false, trackReason?: ContactModalReasons) {
    const allowsPayInStore = this.isPayInStoreAllowedForCountry();

    const overrides: any = {
      showRightClose: true,
      subtitle: this.translate.instant(allowsPayInStore
        ? 'page.identityVerification.eKYCFailure.attemptsExceeded.changeAmountOrSupport.body'
        : 'page.identityVerification.eKYCFailure.attemptsExceeded.supportOnly.body'),
      primaryButtonText: this.translate.instant('page.identityVerification.eKYCFailure.action.contactSupport.label'),
      showZendeskHelpPage: true,
      contactCTAReason: ContactModalReasons.AccountBlockedIdVerification,
      trackEvent: AnalyticsEventType.ContactModalDisplayed,
      trackParams: { location: analyticLocation, reason: trackReason || ContactModalReasons.AccountBlockedIdVerification },
      onClose: () => {
        if (this.suppressFailureClosePop) {
          this.suppressFailureClosePop = false;
          return;
        }
        this.goBackToPersonalDetails();
      }
    };

    if (allowsPayInStore) {
      overrides.showSecondaryButton = true;
      overrides.secondaryButtonText = this.translate.instant('page.identityVerification.eKYCFailure.action.payAndVerifyInStore.label');
      overrides.dismissFromSecondary = false;
      overrides.secondaryButtonAction = async (_rp, modalRef) => {
        await modalRef.viewCtrl.dismiss();
        setTimeout(() => this.goToCalculatorWithPayInStorePreselected(), OnfidoProvider.POST_MODAL_NAVIGATION_DELAY_MS);
      };
      overrides.additionalHyperlinkBtnText = this.translate.instant('page.identityVerification.eKYCFailure.helper.findStoresNearby.text');
      overrides.additionalHyperlinkBtnIcon = 'assets/icons/info-orange.svg';
      overrides.additionalHyperlinkBtnIsPrimary = true;
      overrides.additionalHyperlinkBtnAction = () => {
        this.suppressFailureClosePop = true;
        this.openFindStoresNearby();
      };
    }

    this.openExhaustedFailureAlert(modal, overrides);
  }

  private openExhaustedFailureAlert(modal: boolean, overrides: any) {
    const targetPage = this.routeProvider.pages.ModalAlertPage;
    const targetParams = {
      ...overrides,
      showFullPage: true,
      img: 'assets/imgs/verification-failed.svg',
      title: this.translate.instant('page.identityVerification.eKYCFailure.generic.title')
    };

    if (modal)
      this.routeProvider.openModal(targetPage, targetParams);
    else
      this.routeProvider.replace(targetPage, targetParams);
  }

  private openExchangeRateMayChangeSheet(onConfirm: () => void) {
    this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, {
      image: 'assets/imgs/verification-failed.svg',
      title: this.translate.instant('sendMoney.paymentMethodChange.payInStore.title'),
      subtitle: this.translate.instant('sendMoney.paymentMethodChange.payInStore.body'),
      primaryButtonText: this.translate.instant('sendMoney.paymentMethodChange.payInStore.button'),
      dismissFromPrimary: false,
      primaryButtonAction: async (_rp, modalRef) => {
        await modalRef.viewCtrl.dismiss();
        setTimeout(onConfirm, OnfidoProvider.POST_MODAL_NAVIGATION_DELAY_MS);
      },
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('sendMoney.paymentMethodChange.payInStore.link')
    }, null);
  }

  private goToCalculatorWithPayInStorePreselected() {
    this.routeProvider.setRoot(this.routeProvider.pages.CheckPricesPage, {
      showPaymentMethodChanged: true,
      preSelectedPaymentMethod: PaymentMethodType.StagedRia
    });
  }

  private openFindStoresNearby() {
    const target = this.routeProvider.pages.StoresPageNew || this.routeProvider.pages.StoresPage;
    this.routeProvider.openModal(target, null, null, 'store-locator-modal');
  }

  public async routeExhaustedAttempts(analyticLocation?: string, modal: boolean = false, trackReason?: ContactModalReasons): Promise<void> {
    if (this.isNzEkyc()) {
      this.showNzActionRequired(modal);
      return;
    }

    if (this.ekycType !== KYC_METHODS.VERIFF) {
      LoggerProvider.info(this, 'Current ekycType is not Veriff', { ekycType: this.ekycType });
      return;
    }

    if (this.isFromProfileFlow()) {
      await this.showProfileEkycExhausted(analyticLocation || SegmentLocationNames.Settings, modal, trackReason);
      return;
    }

    if (!this.isFromSendMoneyFlow())
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'routeExhaustedAttempts called without SMF/profile flag set; defaulting to SMF no-skip screen');

    const order = this.orderProvider.getCurrentOrder();
    const skipAllowed = !!order && order.isEKYCSkipAllowed === true && !!order.remainingAmountWithouteKYC && order.remainingAmountWithouteKYC > 0;
    const location = analyticLocation || SegmentLocationNames.SendMoney;

    if (skipAllowed)
      await this.showSmfEkycExhaustedSkipAllowed(location, modal, trackReason);
    else
      await this.showSmfEkycExhaustedNoSkip(location, modal, trackReason);
  }

  releasePromotion() {
    const order = this.orderProvider.getCurrentOrder();
    if(order){
      if(order.PromoTransIdLocked) this.customersProvider.releasePromotion(order.PromoTransIdLocked).subscribe();
      order.PromoTransIdLocked = null;
      this.orderProvider.setCurrentOrder(order);
    }
  }

  setOnfidoFlow(fromSendMoney: boolean, fromProfile: boolean, resetKycResult: boolean = false, fromCarousel: boolean = false) {
    if(!fromSendMoney) this.clearOriginPage(); // reset source to prevent entering Onfido from the send-money flow

    this.isFromSendMoney = fromSendMoney;
    this.isFromProfile = fromProfile;
    this.isFromCarousel = fromCarousel;
    this.preKycIdType = null;
    if (resetKycResult)
      this.resetLastKycResult();
  }

  isFromProfileFlow() {
    return this.isFromProfile;
  }

  isFromSendMoneyFlow() {
    return this.isFromSendMoney;
  }

  async openOnfidoFlow(isModal: boolean, analyticsLocation: string, isSuspected: boolean = false) {
    const isLoading = this.loading.isLoading();
    if (!isLoading)
      this.loading.showDelayed();

    this.preKycIdType = null;
    const identityVerification = await this.loginProvider.getIdentityVerificationAsync();
    const attemptsLeft = identityVerification ? identityVerification.AttemptsLeft : null;

    let page = (attemptsLeft && attemptsLeft > 0) ? this.routeProvider.pages.OnfidoInfoPage : this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF;
    if (await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry())
      page = this.routeProvider.pages.EkycMethodSelectPage;

    let params: any = { analyticsLocation: analyticsLocation };

    if(isSuspected)
     params.onSuccess = () => this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
    
    isModal ? this.routeProvider.openModal(page, params) : this.routeProvider.replace(page, params);

    if (!isLoading)
      this.loading.hide();
  }
  // Veriff calls
  initVeriff(analyticsLocation, orderNumber = null): Promise<any> {
    return new Promise((resolve, reject) => {
      let url = APP_CONFIG.API_URL + 'api/kyc/veriff/initiateverification' + '?workflowstep=' + encodeURIComponent(analyticsLocation);
      if (orderNumber) {
        url = APP_CONFIG.API_URL + 'api/kyc/veriff/initiateverification' + '?workflowstep=' + encodeURIComponent(analyticsLocation) + '&verificationReason=1' + '&orderNo=' + encodeURIComponent(orderNumber);
      } else if (APP_CONFIG.region.region === Region.NZ && this.preKycIdType !== null) {
        url += '&preKycIdType=' + this.preKycIdType;
      }
      this.http.get(url).then(
        res => {
          resolve(res);
        },
        () => reject()
      );
    });
  }

  storeVeriffResult(kycID, orderNumber = null): Promise<any> {
    return new Promise((resolve, reject) => {
      let url = APP_CONFIG.API_URL + 'api/kyc/veriff/endverification'
      if (orderNumber)
        url = APP_CONFIG.API_URL + 'api/kyc/veriff/endverification' + '?verificationReason=1' + '&orderNo=' + encodeURIComponent(orderNumber);
      this.http.post(url, { kycID: kycID }).then(
        res => {
          resolve(res);
        },
        () => reject()
      );
    });
  }

   // Signicat calls
  initiateSignicatVerification(analyticsLocation): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(APP_CONFIG.API_URL + 'api/Kyc/Signicat/InitiateVerification' + '?workflowstep=' + encodeURIComponent(analyticsLocation)).then(
        res => {
          resolve(res);
        },
        () => reject()
      );
    });
  }

  endSignicatVerification(kycID, signicatLink): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${APP_CONFIG.API_URL}api/Kyc/Signicat/EndVerification`, { kycID: kycID, responseString: signicatLink }).then(
        res => {
          resolve(res);
        },
        () => reject()
      );
    });
  }

  useTNInitVerificationCalls() { //we will have different TN api calls for these countries
    if (APP_CONFIG.region.region === Region.UK || APP_CONFIG.region.region === Region.AU || APP_CONFIG.region.region === Region.NZ)
      return true;
   else return false;
  }

  useSocureInitVerificationCalls() { //we will have different Socure api calls for these countries
    if (APP_CONFIG.region.region === Region.US || APP_CONFIG.region.region === Region.PR)
      return true;
   else return false;
  }

  // Trunarrative calls, will eventually replace identification/verifyidentity and BE calls
  initTrunarrative(analyticsLocation): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(APP_CONFIG.API_URL + 'api/kyc/TruNarrative/initiateverification' + '?workflowstep=' + encodeURIComponent(analyticsLocation)).then(
        res => {
          resolve(res);
        },
        () => reject()
      );
    });
  }

  endTrunarrativeVerification(kycID): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(APP_CONFIG.API_URL + 'api/kyc/TruNarrative/endverification', { kycID: kycID }).then(
        res => {
          resolve(res);
        },
        () => reject()
      );
    });
  }

  // only for US currently
  initSocure(analyticsLocation): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(APP_CONFIG.API_URL + 'api/kyc/Socure/initiateverification' + '?workflowstep=' + encodeURIComponent(analyticsLocation)).then(
        res => {
          resolve(res);
        },
        () => reject()
      );
    });
  }

  endSocureVerification(kycID): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(APP_CONFIG.API_URL + 'api/kyc/Socure/endverification', { kycID: kycID }).then(
        res => {
          resolve(res);
        },
        () => reject()
      );
    });
  }


  async showOnfidoVerifyPage(fromProfile: boolean = false, onContinue?: any,missing?:any, fromIdologyPage?: boolean, isUSFallback = false) {
    const fromProfileFlow = fromProfile === true;
    const fromSMFlow = fromProfile === false;

    if (onContinue && !this.routeProvider.hasPage(this.routeProvider.pages.SendSummaryPage) && !fromIdologyPage) {
      LoggerProvider.info(this, 'Has not entered send summary yet hence continue instead of showing EKYC information page');
      onContinue();
      return;
    }
    LoggerProvider.info(this, 'Show EKYC information page');
    const canAccess = await this.canAccessByAttempt();
    if (!canAccess) {
      this.setOnfidoFlow(fromSMFlow, fromProfileFlow);
      await this.routeExhaustedAttempts();
      return;
    }
    else {
      let params = {
        onSuccess: () => {
          if (onContinue)
            onContinue();
          else
            this.routeProvider.replace(this.routeProvider.pages.SendFinishPage);
        },
        onFailure: async () => {
          if (fromIdologyPage)
            this.identityProvider.showIdologyVerifyPage(null, null, onContinue);

          try {
            if (await this.hasAnyZeroAttempts(true, CustomerIdentificationProcessorType.Veriff)) {
              this.onAttemptsExhausted();
            } else
              this.routeProvider.replace(this.getEkycInfoPage(), { ...params, preKycIdType: this.preKycIdType });
          } catch (ex) {
            LoggerProvider.exception(this, ex, LoggerEvent.FailedVerifyIdentity, "Unable to get attempts");
            this.onAttemptsExhausted();
          }
        },
        analyticsLocation: SegmentLocationNames.SendMoney,
        missing: missing,
        isUSFallback: isUSFallback // use ekyc fallback country setting for US
      };
      this.setOnfidoFlow(fromSMFlow, fromProfileFlow);
      if (await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry())
        this.routeProvider.replace(this.routeProvider.pages.EkycMethodSelectPage, params);
      else
        this.redirectToOnFidoInfoPage(fromSMFlow, params, missing, isUSFallback);
    }
  }

  // has user already gone through Onfido/Veriff, doesn't matter if pass/fail
  isEkycEnableUS(customer) {
    let isEkycUSEnable = false;
    const kycProcessors = customer.identification.identityVerificationProcessors || [];
    const kycProcessorFiltered = kycProcessors.filter((obj: ICustomerIdentificationProcessor) => obj.processor === CustomerIdentificationProcessorType.Veriff || obj.processor === CustomerIdentificationProcessorType.Onfido)
    if (kycProcessorFiltered.length > 0) {
      isEkycUSEnable = true;
    }
    return isEkycUSEnable;
  }

  async checkOnfidoIdentification() {
    const isOnfidoEnable = await this.isOnfidoEnable();
    if (isOnfidoEnable)
      return true;
    if (APP_CONFIG.region.region === Region.US) {
      const customer = await this.customerProvider.getCustomer();
      const onfidoStatus = this.isEkycEnableUS(customer);
      if (onfidoStatus)
        return true;
    }
    return false;
  }

  getSignicatURL(url) {
    let urlSignicat = 'riamoneytransfer://deeplink/SIGNICAT';
    if(url.indexOf('riamoneytransfer://deeplink/signicat') > -1) {
      urlSignicat = 'riamoneytransfer://deeplink/signicat';
    }

    return urlSignicat;
  }

/**
 * 
 * @param data branch payload
 * @param isHttpsLink refers if redirect_uri is universal https or custom scheme
 * @param url callback from ASWebAuthSession for ios MitID
 */
  processSignicatDeeplink(data, isHttpsLink = false, url = null) {
    var link;     // URLSearchParams doesn't without stripping the string
    if (isHttpsLink) {
      LoggerProvider.info(this, 'Signicat deeplink is https');
      if (data['~referring_link'].indexOf('https://riamoneytransfer.test-app.link/') > -1)
        link = data['~referring_link'].replace('https://riamoneytransfer.test-app.link/', '')
      else if (data['~referring_link'].indexOf('https://riamoneytransfer.app.link/') > -1)
        link = data['~referring_link'].replace('https://riamoneytransfer.app.link/', '')
      else if (data['+non_branch_link'].indexOf('https://riamoneytransfer.app.link/') > -1)
        link = data['+non_branch_link'].replace('https://riamoneytransfer.app.link/', '')
    }
    else if (url) {
      LoggerProvider.info(this, 'Signicat deeplink is custom scheme from ASWebAuthSession');
      const signicatURL = this.getSignicatURL(url);
      link = url.replace(signicatURL, '');
    }
    else if (!isHttpsLink && data) {
      LoggerProvider.info(this, 'Signicat deeplink is custom scheme');
      const signicatURL = this.getSignicatURL(data['+non_branch_link']);
      link = data['+non_branch_link'].replace(signicatURL, '');
    }
    LoggerProvider.info(this, 'Processed Signicat link is ' + link);
    const code = new URLSearchParams(link).get("code");
    const sessionState = new URLSearchParams(link).get("session_state");
    const state = new URLSearchParams(link).get("state");
    const error = new URLSearchParams(link).get("error");
    const error_description = new URLSearchParams(link).get("error_description");
    if (error) {
      LoggerProvider.error(this, LoggerEvent.Signicat, 'Signicat deep link error ' + error);
      LoggerProvider.error(this, LoggerEvent.Signicat, 'Signicat deep link error message' + error_description);
    }
    if (error_description && error_description.includes("abort")) {
      LoggerProvider.error(this, LoggerEvent.Signicat, 'Signicat User aborted');
      this.routeProvider.replace(this.routeProvider.pages.OnfidoServerTimeoutErrorPage)
    }
    else
      this.routeProvider.replace(this.routeProvider.pages.SignicatVerifyPage, {
        signicatLink: link,
        code: code, sessionState: sessionState,
        state: state,
        error: error,
        error_description: error_description,
        onSuccess: this.previousOnSuccess,
        analyticsLocation: this.previousAnalyticsLocation,
        fromSettingsPage: this.previousFromSettingsPage
      })
  }

  async getCustomerEkycAllowedDocuments(): Promise<string[]> {
   let user =  await this.loginProvider.ensureInitializedUser(true);
    if (user && user.Customers && user.Customers.length > 0 && user.Customers[0].eKYCAllowedDocuments) {
      const documentsAllowed = user.Customers[0].eKYCAllowedDocuments;
      const listofAllowedDocuments = documentsAllowed.split(",");
      const translatedDocumentTypes = [];
      listofAllowedDocuments.forEach((documentType)=> {
        switch(documentType) {
          case AllowedDocuments.NATIONAL_IDENTITY_CARD:
            if (APP_CONFIG.region.region === Region.CL) {
              translatedDocumentTypes.push("page.identityVerificationPage.nationalId_CL");
              translatedDocumentTypes.push("page.identityVerificationPage.mercosurId_CL");
            } else
              translatedDocumentTypes.push("page.identityVerificationPage.nationalId");
            break;
          case AllowedDocuments.DRIVING_LICENCE:
            translatedDocumentTypes.push("page.identityVerificationPage.driversLicense");
            break;
          case AllowedDocuments.PASSPORT:
            translatedDocumentTypes.push("global.myRia.jsonContent.OnfidoDocument_passport");
            break;
          case AllowedDocuments.RESIDENCE_PERMIT:
            if (APP_CONFIG.region.region === Region.CL) {
              translatedDocumentTypes.push("page.identityVerificationPage.foreignerId_CL");
             }
            else {
            translatedDocumentTypes.push("global.myRia.jsonContent.OnfidoDocument_residence_permit");
            }
            break;
        }
      })
      return translatedDocumentTypes;
    }
    return null;
  }

  resetLastKycResult() {
    AuthProvider.lastKycResult = KYC_ATTEMPT_STATUS.IN_PROGRESS;
  }

  isLastKycResultFinished() {
    return (AuthProvider.lastKycResult === KYC_ATTEMPT_STATUS.VERIFIED);
  }

  // TruNarrative ends on its own success screen, so skip the Veriff one when it still has to run
  async onVeriffVerified(onSuccess) {
    if (this.isNzEkyc()) {
      try {
        if (await this.isTruNarrativeRequired()) {
          this.identityProvider.showIdologyVerifyPage(null, null, onSuccess);
          return;
        }
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to read mandatory KYC processors after Veriff');
      }
    }
    this.showOnfidoSuccessPage(onSuccess);
  }

  // false when the list is missing, so a backend without it keeps today's flow (success, then order validate routes to TruNarrative)
  async isTruNarrativeRequired(): Promise<boolean> {
    const user = await this.loginProvider.getUserAsync(true);
    const processors = user && user.MandatoryIdentityVerificationProcessors;
    if (!processors)
      return false;

    const truNarrative = processors.find(p => p.ProcessorId === EKYC_PROCESSOR_TYPE.TruNarrative);
    return !!truNarrative
      && truNarrative.Status !== CustomerIdentificationStatusType.Verified
      && truNarrative.Status !== CustomerIdentificationStatusType.NotRequired;
  }

  showOnfidoSuccessPage(onSuccess) {
    // nothing blocks Continue on the wallet path, so a double tap must not push and pop twice
    let walletContinueStarted = false;
    this.routeProvider.replace(this.routeProvider.pages.OnfidoVerifiedPage, {
      primaryButtonAction: async () => {
        // Wallet onboarding: the bridge onSuccess refreshes the profile itself behind the
        // Vue loading screen, so no native loader or profile call here.
        if (EkycFlowState.isWalletOnboarding && onSuccess) {
          if (walletContinueStarted) {
            return;
          }
          walletContinueStarted = true;
          onSuccess();
          return;
        }
        this.loading.showDelayed();
        try {
          const user = await this.loginProvider.getUserAsync(true); // In case of customer link, We get the customer correctly in CST profile call as public profile call takes some time to get updated
          // update the updated customerId in the customer object for now
          // TODO!! Check with BE, to properly map the responses from CST profile call.. Some properties are only available in public profile call
          const userCustomer = this.loginProvider.getUserCustomer(user);
          if (this.customerProvider.customer && userCustomer) {
            this.customerProvider.customer.id = userCustomer.CustomerId;
          }
        } catch (ex) {
          this.loading.hide();
          LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get customer in verified page');
        }
        this.loading.hide();
        if (onSuccess)
          onSuccess();
      }
    });
  }

  private getProcessorName (key: number) {
    if (key === null || key === undefined)
        return null;

    return EKYC_PROCESSOR[key] || null;
  }

  // Use this to set the origin page for Onfido flow
  // TODO: Can later remove previousFromSettingsPage by using this too
  setOriginPage(page: INavInfo): void {
    this.triggerSourcePage = page;
  }

  getOriginPage(): INavInfo | null {
    return this.triggerSourcePage;
  }

  clearOriginPage(): void {
    this.triggerSourcePage = null;
  }


  private onAttemptsExhausted() {
    if (this.isNzEkyc()) {
      this.showNzActionRequired();
    } else if (APP_CONFIG.region.region === Region.NZ) {
      this.routeProvider.replace(this.routeProvider.pages.UnableToVerifyGoToStorePage, {
        msg: this.translate.instant('page.identityVerificationPage.attemptsReachedBody')});
    } else {
      this.routeProvider.replace(this.routeProvider.pages.ModalAlertPage, {
        image: 'assets/imgs/exclamation_red.svg',
        title: this.translate.instant('page.sendFailedPage.sendVerifyFailedHeader'),
        subtitle: this.translate.instant('page.walletReceiveMoneyPage.visitHelpCenterBody'),
        primaryButtonText: this.translate.instant('global.gotItButton'),
        primaryAction: () => {
          if(this.routeProvider.hasPage(this.routeProvider.pages.CheckPricesPage))
            this.routeProvider.popTo(this.routeProvider.pages.CheckPricesPage);
          else
            this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
        },
        showFullPage: true,
        hideHeader: true
      });
    }
  }

  getEkycInfoPage(): INavInfo {
    if (this.isNzEkyc())
      return this.routeProvider.pages.NzDocumentSelectionPage;
    return this.routeProvider.pages.OnfidoInfoPage;
  }

  private async redirectToOnFidoInfoPage(fromSMFlow, onFidoPageParams, missing, isUSFallback) {
    const identityVerification = await this.getIdentityVerification(missing, isUSFallback);
    const attemptsLeft = identityVerification ? identityVerification.AttemptsLeft : null;

    if (attemptsLeft && attemptsLeft > 0) {
      this.routeProvider.replace(this.getEkycInfoPage(), onFidoPageParams);
      return;
    }

    await this.routeExhaustedAttempts();
  }

  async getIdentityVerification(missing, isUSFallback): Promise<IdentityVerification> {
    let identityVerification = await this.loginProvider.getIdentityVerificationAsync(true);
    const hasMultipleEkycProviders = await this.loginProvider.hasMultipleEkycProviders();

    if (hasMultipleEkycProviders) {
      const fallbackEkycType = await this.showVeriffOrOnfido(missing, isUSFallback); // if multiple ekyc providers, then match with veriff/onfido
      const fallbackEkycNumber = this.loginProvider.getKYCProcessorNumber(fallbackEkycType);
      const identityVerifications = await this.loginProvider.getIdentityVerificationsAsync();
      const element = identityVerifications.find(e => e.Processor === fallbackEkycNumber);

      if (element)
        identityVerification = element;
    }

    return identityVerification;
  }
}


// WEBPACK FOOTER //
// ./src/providers/myria/onfido-provider.ts